// The review family's CLOSE-THE-LOOP kinds (plan 102 slice 07, #7553): the issue and PR
// ENDS the platform writes on GitHub, and the branch tidy after them (slice 07b). Split out of `review.ts` for size, like
// `./review-knowledge.ts`, and spread into the same composed table; `review.test.ts` proves
// the halves disjoint. Payload contracts: `../../audit/payload-close-loop.ts`. Each render
// reads the payload defensively (`./review-payload` accessors) and falls back to the repo
// line, never a fabricated number: a withheld row carries no payload.

import type { TerminalRenderContext, TerminalRendered } from '../../../wire/terminal';
import { terminalRendererGroup } from '../../../wire/terminal';
import { numOpt, prSubject, rec, refsFor, repoSubject, strOpt, strOr } from './review-payload';

function issueSubject(repo: string | null, issue: number | null, sessionId: string) {
  if (repo !== null && issue !== null) {
    const label = `${repo}#${issue}`;
    return { kind: 'issue' as const, id: label, label };
  }
  return repoSubject(repo, sessionId);
}

// ─── issue.closed_final (decision) ───────────────────────────────────────────
function issueClosedFinal(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const issue = numOpt(input['issueNumber']);
  return {
    line: {
      key: 'terminal.issue.closed_final',
      params: { reason: strOr(output['reason'], 'unknown') },
    },
    subject: issueSubject(repo, issue, ctx.row.sessionId),
    refs: refsFor({ repo, issue, sessionId: ctx.row.sessionId }),
  };
}

// ─── issue.diagnosis_posted (info) ───────────────────────────────────────────
function issueDiagnosisPosted(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const issue = numOpt(input['issueNumber']);
  return {
    line: {
      key: 'terminal.issue.diagnosis_posted',
      params: { followUp: strOr(output['followUp'], 'none') },
    },
    subject: issueSubject(repo, issue, ctx.row.sessionId),
    refs: refsFor({
      repo,
      issue,
      taskId: strOpt(input['taskId']),
      sessionId: ctx.row.sessionId,
    }),
  };
}

// ─── issue.escalation_posted (decision) ──────────────────────────────────────
function issueEscalationPosted(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const issue = numOpt(input['issueNumber']);
  return {
    line: { key: 'terminal.issue.escalation_posted', params: {} },
    subject: issueSubject(repo, issue, ctx.row.sessionId),
    refs: refsFor({ repo, issue, sessionId: ctx.row.sessionId }),
  };
}

// ─── pr.self_refiled (decision) ──────────────────────────────────────────────
function prSelfRefiled(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const pr = numOpt(input['prNumber']);
  const gatePaths = Array.isArray(output['gatePaths']) ? output['gatePaths'].length : 0;
  return {
    line: { key: 'terminal.pr.self_refiled', params: { gatePaths } },
    subject: prSubject(repo, pr, ctx.row.sessionId),
    refs: refsFor({ repo, pr, taskId: strOpt(input['taskId']), sessionId: ctx.row.sessionId }),
  };
}

// ─── pr.superseded_closed (decision) ─────────────────────────────────────────
function prSupersededClosed(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const pr = numOpt(input['prNumber']);
  const sha = strOpt(output['recoveredSha']);
  return {
    line: {
      key: 'terminal.pr.superseded_closed',
      params: { sha: sha === null ? 'unknown' : sha.slice(0, 7) },
    },
    subject: prSubject(repo, pr, ctx.row.sessionId),
    refs: refsFor({ repo, pr, taskId: strOpt(input['taskId']), sessionId: ctx.row.sessionId }),
  };
}

// ─── external_intake.graded (decision) ───────────────────────────────────────
function externalIntakeGraded(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const pr = numOpt(input['prNumber']);
  return {
    line: {
      key: 'terminal.external_intake.graded',
      params: {
        rule: strOr(output['rule'], 'unknown'),
        decision: strOr(output['decision'], 'unknown'),
      },
    },
    subject: prSubject(repo, pr, ctx.row.sessionId),
    refs: refsFor({ repo, pr, taskId: strOpt(input['taskId']), sessionId: ctx.row.sessionId }),
  };
}

// ─── the external ban's kinds (#7785 part 3) ─────────────────────────────────
// One line per row, the item's enum facts only: no row holds author text.
const itemSubject = (ctx: TerminalRenderContext) => {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const number = numOpt(input['itemNumber']) ?? numOpt(input['prNumber']);
  const { sessionId } = ctx.row;
  const issue = input['itemKind'] === 'issue';
  return {
    subject: issue
      ? issueSubject(repo, number, sessionId)
      : number === null
        ? repoSubject(repo, sessionId)
        : prSubject(repo, number, sessionId),
    refs: refsFor({ repo, ...(issue ? { issue: number } : { pr: number }), sessionId }),
  };
};

function externalDangerJudged(ctx: TerminalRenderContext): TerminalRendered {
  const output = rec(ctx.row.output);
  const { subject, refs } = itemSubject(ctx);
  return {
    line: {
      key: 'terminal.external_danger.judged',
      params: {
        verdict: strOr(output['verdict'], 'unknown'),
        read: strOr(output['read'], 'first'),
      },
    },
    subject,
    refs,
  };
}

function externalBanEnforced(ctx: TerminalRenderContext): TerminalRendered {
  const output = rec(ctx.row.output);
  const { subject, refs } = itemSubject(ctx);
  return {
    line: {
      key: 'terminal.external_ban.enforced',
      params: {
        rule: strOr(output['rule'], 'unknown'),
        action: strOr(output['action'], 'unknown'),
      },
    },
    subject,
    refs,
  };
}

function externalBanRereviewFiled(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const { subject, refs } = itemSubject(ctx);
  return {
    line: {
      key: 'terminal.external_ban.rereview_filed',
      params: { user: String(numOpt(input['githubUserId']) ?? 0) },
    },
    subject,
    refs,
  };
}

/** A list change: the subject is the account's chain, since it names no repo. */
const externalBanListRow =
  (key: 'added' | 'removed' | 'promoted' | 'write_refused') =>
  (ctx: TerminalRenderContext): TerminalRendered => {
    const input = rec(ctx.row.input);
    return {
      line: {
        key: `terminal.external_ban.${key}`,
        params: {
          user: String(numOpt(input['githubUserId']) ?? 0),
          list: strOr(input['list'], 'shared'),
        },
      },
      subject: repoSubject(ctx.row.repoFullName, ctx.row.sessionId),
      refs: refsFor({ repo: ctx.row.repoFullName, pr: null, sessionId: ctx.row.sessionId }),
    };
  };

// ─── branch.deleted (decision) ───────────────────────────────────────────────
// Subject: the PR whose head it was, else the repo (a task's `dz/` branch had no PR).
function branchDeleted(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const pr = numOpt(output['prNumber']);
  return {
    line: {
      key: 'terminal.branch.deleted',
      params: {
        branch: strOr(input['branch'], 'unknown'),
        trigger: strOr(output['trigger'], 'unknown'),
      },
    },
    subject:
      pr === null ? repoSubject(repo, ctx.row.sessionId) : prSubject(repo, pr, ctx.row.sessionId),
    refs: refsFor({ repo, pr, taskId: strOpt(output['taskId']), sessionId: ctx.row.sessionId }),
  };
}

/** This sub-table's entries, spread into `review.ts`'s composed export. */
export const TERMINAL_RENDERERS_REVIEW_CLOSE_LOOP = terminalRendererGroup({
  'issue.closed_final': { level: 'decision', actor: 'system', render: issueClosedFinal },
  'issue.diagnosis_posted': { level: 'info', actor: 'system', render: issueDiagnosisPosted },
  'issue.escalation_posted': { level: 'decision', actor: 'system', render: issueEscalationPosted },
  'pr.self_refiled': { level: 'decision', actor: 'babysitter', render: prSelfRefiled },
  'pr.superseded_closed': { level: 'decision', actor: 'babysitter', render: prSupersededClosed },
  'external_intake.graded': {
    level: 'decision',
    actor: 'babysitter',
    render: externalIntakeGraded,
  },
  'external_danger.judged': { level: 'info', actor: 'system', render: externalDangerJudged },
  'external_ban.added': { level: 'decision', actor: 'system', render: externalBanListRow('added') },
  'external_ban.removed': {
    level: 'decision',
    actor: 'system',
    render: externalBanListRow('removed'),
  },
  'external_ban.write_refused': {
    level: 'info',
    actor: 'system',
    render: externalBanListRow('write_refused'),
  },
  'external_ban.enforced': { level: 'decision', actor: 'system', render: externalBanEnforced },
  'external_ban.promoted': {
    level: 'decision',
    actor: 'system',
    render: externalBanListRow('promoted'),
  },
  'external_ban.rereview_filed': {
    level: 'info',
    actor: 'system',
    render: externalBanRereviewFiled,
  },
  'branch.deleted': { level: 'decision', actor: 'system', render: branchDeleted },
});
